import { ChevronDownIcon } from '@heroicons/react/solid';
import { omit } from 'lodash';
import React, { ComponentProps, FunctionComponent } from 'react';
import { twMerge } from 'tailwind-merge';

type IInputProps = {
  id?: string;
  type?: 'text' | 'number' | 'email' | 'password' | 'file' | 'submit';
  label?: string;
  className?: string;
  error?: string;
  placeholder?: string;
  onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  extraInfo?: string;
  isViewMode?: boolean;
  labelClassName?: string;
};

type TInputType = IInputProps & ComponentProps<'input'>;

export const Input: FunctionComponent<TInputType> = ({
  id,
  type = 'text',
  name,
  label,
  className,
  onChange,
  error,
  placeholder,
  extraInfo,
  isViewMode,
  labelClassName,
  ...rest
}) => {
  let inputStyle =
    'inline-flex w-full px-4 py-2 h-[45px] rounded-lg text-primary-900 focus:opacity-100 justify-center inline-flex items-center disabled:opacity-40 focus:border-brand-600 border-1';

  if (rest.icon) {
    inputStyle = twMerge(inputStyle, 'pl-10');
  }

  // Add padding for ChevronDown icon on the right
  inputStyle = twMerge(inputStyle, 'pr-10');

  if (error) {
    inputStyle = twMerge(inputStyle, 'border-red-500 hover:border-red-500 focus:border-red-500 outline-none');
  }

  if (rest.disabled) {
    inputStyle = twMerge(inputStyle, 'bg-gray-200 text-gray-500');
  }

  const viewModeProps = { type };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className='inline-flex w-full flex-col text-left'>
      {label && (
        <label htmlFor={name} className={twMerge('block text-sm font-medium text-gray-700 py-2', labelClassName)}>
          {label}
        </label>
      )}
      <div className='relative w-auto rounded-md'>
        {rest.icon && (
          <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
            <rest.icon className='mr-4 size-6 shrink-0 text-gray-500' />
          </div>
        )}
        <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3'>
          <ChevronDownIcon className='size-5 text-gray-400' aria-hidden='true' />
        </div>
        {isViewMode ? (
          <div {...viewModeProps} className={className ? twMerge(inputStyle, className) : inputStyle}>
            {rest.value}
          </div>
        ) : (
          <input
            {...omit(rest, 'icon')}
            id={id}
            name={name}
            type={type}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={rest.disabled || isViewMode}
            className={className ? twMerge(inputStyle, className) : inputStyle}
          />
        )}
      </div>
      {error && <p className='mt-2 text-sm text-red-600'>{error}</p>}
      {extraInfo && !error && <p className='mt-2 text-sm text-gray-500'>{extraInfo}</p>}
    </div>
  );
};
